import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Pagination,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DocumentSnapshot } from 'firebase/firestore';
import { Person } from '../types';
import { PersonService } from '../services/personService';
import { useAuth } from '../contexts/AuthContext';
import { PeopleGrid } from '../components/people/PeopleGrid';
import { PeopleList } from '../components/people/PeopleList';
import { PeopleTable } from '../components/people/PeopleTable';
import { ViewModeToggle } from '../components/people/ViewModeToggle';
import { FilterDrawer } from '../components/people/FilterDrawer';
import { SearchBar } from '../components/people/SearchBar';
import { usePeopleFilters } from '../hooks/usePeopleFilters';

export type ViewMode = 'grid' | 'list' | 'table';
export type SortOption = 'nameAsc' | 'nameDesc' | 'birthDateAsc' | 'birthDateDesc' | 'recentlyAdded';

/**
 * People Page
 *
 * Main interface for browsing all family members
 *
 * Features:
 * - Three view modes: Grid, List, Table
 * - Pagination (50 per page)
 * - Search/filter controls
 * - Sort options
 * - Add Person button (navigates to separate page)
 * - Empty state for new users
 *
 * Data Source: PersonService.getPersons()
 */
export const People: React.FC = () => {
  // ===== Hooks =====
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authState } = useAuth();

  // ===== State =====
  const [persons, setPersons] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('peopleViewMode');
    return (saved as ViewMode) || 'grid';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>('nameAsc');
  const [pageDocuments, setPageDocuments] = useState<Map<number, DocumentSnapshot | undefined>>(new Map([[1, undefined]]));
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState<number>(0);

  // ===== Custom Hooks =====
  const { filters, setFilters, filteredPersons, activeFilterCount } = usePeopleFilters(persons);

  // ===== Constants =====
  const ITEMS_PER_PAGE = 50;

  // ===== Effects =====
  useEffect(() => {
    loadPersons();
  }, [currentPage, sortOption]);

  useEffect(() => {
    // Load total count on mount
    const loadTotalCount = async () => {
      try {
        const count = await PersonService.getPersonCount();
        setTotalCount(count);
      } catch (err) {
        console.error('Failed to load total count:', err);
      }
    };
    loadTotalCount();
  }, []);

  useEffect(() => {
    // Save view mode preference
    localStorage.setItem('peopleViewMode', viewMode);
  }, [viewMode]);

  // ===== Helper Functions =====
  const loadPersons = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the lastDoc for the current page
      const lastDoc = pageDocuments.get(currentPage) || undefined;

      // Get persons from service with pagination
      const result = await PersonService.getPersons(ITEMS_PER_PAGE, lastDoc);

      // Apply sorting
      const sorted = sortPersons(result.persons, sortOption);

      setPersons(sorted);
      setHasMore(result.hasMore);

      // Store the lastDoc for the next page if there are more results
      if (result.hasMore && result.lastDoc) {
        const newPageDocs = new Map(pageDocuments);
        newPageDocs.set(currentPage + 1, result.lastDoc);
        setPageDocuments(newPageDocs);
      }

      // Update total pages estimate
      if (!result.hasMore) {
        setTotalPages(currentPage);
      } else if (totalPages < currentPage + 1) {
        setTotalPages(currentPage + 1);
      }
    } catch (err) {
      console.error('Failed to load persons:', err);
      setError('Failed to load family members. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sortPersons = (personsArray: Person[], sort: SortOption): Person[] => {
    const sorted = [...personsArray];

    switch (sort) {
      case 'nameAsc':
        return sorted.sort((a, b) => {
          const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
          const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
      case 'nameDesc':
        return sorted.sort((a, b) => {
          const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
          const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
          return nameB.localeCompare(nameA);
        });
      case 'birthDateAsc':
        return sorted.sort((a, b) => {
          if (!a.birthDate) return 1;
          if (!b.birthDate) return -1;
          return a.birthDate.localeCompare(b.birthDate);
        });
      case 'birthDateDesc':
        return sorted.sort((a, b) => {
          if (!a.birthDate) return 1;
          if (!b.birthDate) return -1;
          return b.birthDate.localeCompare(a.birthDate);
        });
      case 'recentlyAdded':
        return sorted.sort((a, b) => {
          return b.createdAt.getTime() - a.createdAt.getTime();
        });
      default:
        return sorted;
    }
  };

  // ===== Event Handlers =====
  const handleViewModeChange = (newMode: ViewMode) => {
    setViewMode(newMode);
  };

  const handlePersonClick = (personId: string) => {
    navigate(`/people/${personId}`);
  };

  const handleAddPerson = () => {
    navigate('/people/add');
  };

  const handleFilterToggle = () => {
    setFilterDrawerOpen(!filterDrawerOpen);
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleFilterRemove = (filterKey: string) => {
    setFilters((prev: any) => {
      const updated = { ...prev };
      delete updated[filterKey];
      return updated;
    });
  };

  const handleClearAllFilters = () => {
    setFilters({});
  };

  const handleSearchChange = (query: string) => {
    setFilters((prev) => ({ ...prev, searchQuery: query }));
    setCurrentPage(1); // Reset to first page when search changes
  };

  const handleSortChange = (newSort: SortOption) => {
    setSortOption(newSort);
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ===== Computed Values =====
  const displayPersons = filteredPersons;
  const hasPersons = persons.length > 0;
  const hasFilteredResults = filteredPersons.length > 0;

  // ===== Render Helper Functions =====
  const renderViewContent = () => {
    if (!hasFilteredResults && activeFilterCount > 0) {
      return (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Results Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            No family members match your current filters. Try adjusting or clearing your filters.
          </Typography>
          <Button
            variant="outlined"
            onClick={handleClearAllFilters}
            startIcon={<FilterListIcon />}
          >
            Clear All Filters
          </Button>
        </Paper>
      );
    }

    switch (viewMode) {
      case 'grid':
        return (
          <PeopleGrid
            persons={displayPersons}
            onPersonClick={handlePersonClick}
          />
        );
      case 'list':
        return (
          <PeopleList
            persons={displayPersons}
            onPersonClick={handlePersonClick}
          />
        );
      case 'table':
        return (
          <PeopleTable
            persons={displayPersons}
            onPersonClick={handlePersonClick}
            onSortChange={handleSortChange}
            currentSort={sortOption}
          />
        );
      default:
        return null;
    }
  };

  const renderActiveFilters = () => {
    if (activeFilterCount === 0) return null;

    const filterChips: JSX.Element[] = [];

    if (filters.searchQuery && filters.searchQuery.trim().length > 0) {
      filterChips.push(
        <Chip
          key="searchQuery"
          label={`Search: "${filters.searchQuery}"`}
          onDelete={() => handleFilterRemove('searchQuery')}
          size="small"
        />
      );
    }

    if (filters.gender) {
      filterChips.push(
        <Chip
          key="gender"
          label={`Gender: ${filters.gender}`}
          onDelete={() => handleFilterRemove('gender')}
          size="small"
        />
      );
    }

    if (filters.isDeceased !== undefined) {
      filterChips.push(
        <Chip
          key="isDeceased"
          label={filters.isDeceased ? 'Deceased' : 'Living'}
          onDelete={() => handleFilterRemove('isDeceased')}
          size="small"
        />
      );
    }

    if (filters.birthYearFrom || filters.birthYearTo) {
      filterChips.push(
        <Chip
          key="birthYear"
          label={`Birth Year: ${filters.birthYearFrom || '?'} - ${filters.birthYearTo || '?'}`}
          onDelete={() => {
            handleFilterRemove('birthYearFrom');
            handleFilterRemove('birthYearTo');
          }}
          size="small"
        />
      );
    }

    if (filters.hasPhoto !== undefined) {
      filterChips.push(
        <Chip
          key="hasPhoto"
          label={filters.hasPhoto ? 'Has Photo' : 'No Photo'}
          onDelete={() => handleFilterRemove('hasPhoto')}
          size="small"
        />
      );
    }

    if (filters.hasBiography !== undefined) {
      filterChips.push(
        <Chip
          key="hasBiography"
          label={filters.hasBiography ? 'Has Biography' : 'No Biography'}
          onDelete={() => handleFilterRemove('hasBiography')}
          size="small"
        />
      );
    }

    if (filters.lastName) {
      filterChips.push(
        <Chip
          key="lastName"
          label={`Last Name: ${filters.lastName}`}
          onDelete={() => handleFilterRemove('lastName')}
          size="small"
        />
      );
    }

    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
        {filterChips}
        {filterChips.length > 1 && (
          <Button
            size="small"
            onClick={handleClearAllFilters}
            sx={{ ml: 1 }}
          >
            Clear All
          </Button>
        )}
      </Stack>
    );
  };

  // ===== Render: Loading State =====
  if (loading && persons.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // ===== Render: Empty State =====
  if (!loading && !hasPersons) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Welcome to Your Family Tree
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            You haven't added any family members yet. Start building your family tree by adding your first person!
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={handleAddPerson}
          >
            Add Your First Family Member
          </Button>
        </Paper>
      </Container>
    );
  }

  // ===== Render: Main Content =====
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Family Members
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPerson}
          >
            Add Person
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {totalCount > 0 ? (
            <>
              {/* Calculate cumulative count: previous pages + current page items */}
              {((currentPage - 1) * ITEMS_PER_PAGE) + displayPersons.length} / {totalCount} {totalCount === 1 ? 'person' : 'people'}
              {activeFilterCount > 0 && ` (${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'} active)`}
            </>
          ) : (
            <>
              {displayPersons.length} {displayPersons.length === 1 ? 'person' : 'people'}
              {activeFilterCount > 0 && ` (${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'} active)`}
            </>
          )}
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <SearchBar
          value={filters.searchQuery || ''}
          onChange={handleSearchChange}
          placeholder="Search people by name..."
        />
      </Box>

      {/* Controls Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={handleFilterToggle}
        >
          Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
        </Button>

        <ViewModeToggle
          viewMode={viewMode}
          onChange={handleViewModeChange}
        />
      </Box>

      {/* Active Filters */}
      {renderActiveFilters()}

      {/* View Content */}
      {renderViewContent()}

      {/* Pagination */}
      {hasFilteredResults && (hasMore || currentPage > 1) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={hasMore ? currentPage + 1 : currentPage}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton={!hasMore}
            hidePrevButton={currentPage === 1}
            hideNextButton={!hasMore && currentPage === totalPages}
          />
        </Box>
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearAllFilters}
        persons={persons}
      />
    </Container>
  );
};

export default People;
